import React, { useMemo } from "react";
import Background from "../Background";
import ContainerEffectOverlay from "../ContainerEffectOverlay";
import ProgressiveHeroImage from "../ProgressiveHeroImage";
import {
  SlotRenderer,
  EmptyMediaSlotAffordance,
  SlotCapacityAffordance,
  EditableSlotMediaOverlay,
  getHeroImageStyle,
  useHeroAnimation,
  useHeroAmbient,
} from "./heroModeUtils";
import "./heroModeAnimations.css";
import "./heroPhotoDrift.css";

/**
 * HeroPhotoDrift — the Photo Drift family renderer. Title / body /
 * CTA flow at the top of the hero (driving the hero's height) and a
 * scattered, depth-layered photo track sits as a **backdrop layer**
 * behind the text — z-positioned between the hero's background
 * layer and the foreground content, occupying the lower portion of
 * the hero via a top-fade CSS mask so it reads as part of the
 * scenery rather than a stacked block beneath the text.
 *
 * Why a backdrop layer (not a stacked column):
 *   * The earlier draft mounted the photo track as a sibling block
 *     in a flex column with the text — which forced the hero's
 *     height to be ``text + trackVh``, making the hero taller than
 *     the user's content needed.
 *   * Mounting the track as ``position: absolute; inset: 0`` at
 *     ``z-[1]`` lets the hero's height collapse back to whatever
 *     the text content demands. The photos still appear "below"
 *     the text visually because the CSS mask fades the upper
 *     portion of the photo layer to transparent, so the user's
 *     background bleeds through where the text sits.
 *
 * The composition (text on top, photos as backdrop beneath) is
 * identical across every member of the family; what changes is the
 * **drift axis**, which is encoded in ``item.hero_mode``:
 *
 *   * ``photo_drift_up``    — vertical column of photos (two lanes
 *                             side-by-side) translating upward on
 *                             the ``hero-ambient-photo-drift-up``
 *                             keyframe. The duplicate-children copy
 *                             re-enters from the bottom edge so the
 *                             column reads as a continuous feed
 *                             always rolling up.
 *   * ``photo_drift_left``  — horizontal band of photos (two rows
 *                             stacked) translating leftward on
 *                             ``hero-ambient-photo-drift-left``.
 *                             The duplicate re-enters from the
 *                             right edge so the strip reads as a
 *                             newsreel passing from right to left.
 *   * ``photo_drift_right`` — mirror of ``_left``: the keyframe
 *                             starts shifted ``-50%`` and animates
 *                             back to 0 so the band visibly drifts
 *                             toward the right edge with the
 *                             duplicate re-entering from the left.
 *
 * Per-photo "depth scattering": each photo carries a small lateral
 * offset (perpendicular to the drift axis) and a per-photo scale
 * (the canonical ``DEPTH_SCALES`` chain interleaves "near" and
 * "far" values across the index sequence so adjacent photos always
 * differ in depth, never read as a tidy grid). The lateral offset
 * + scale are applied as inline styles on each photo wrapper so
 * they stay attached to the moving track and don't fight the CSS
 * animation.
 *
 * Backward compat note: this file only ships under the three new
 * ``photo_drift_*`` mode ids, all of which are introduced together
 * — there is no legacy mode being migrated into this renderer, so
 * existing user heroes are unaffected by its addition.
 */

// Mode-id constants — the renderer keys off ``item.hero_mode`` to
// pick the lane geometry (vertical-two-column for the upward
// sibling, horizontal-two-row for the lateral siblings). Only the
// upward id needs an explicit comparison branch because the
// lateral siblings share the same horizontal-band layout — both
// fall through the ``!isVertical`` branch. Keeping the upward id
// as a named constant (rather than a scattered string literal)
// makes the family contract obvious in one place; if a future
// direction sibling joins (e.g. ``photo_drift_diagonal``) the
// constant block grows here first and the rest of the renderer
// can branch off the new id.
const MODE_DRIFT_UP = "photo_drift_up";

// ---------------------------------------------------------------------------
// Per-breakpoint tuning
// ---------------------------------------------------------------------------
//
// ``contentPx`` / ``contentPy`` — outer padding for the text
//   column that sits on top of the photo backdrop. Generous
//   vertical padding so the title/body/CTA have comfortable
//   breathing room from the implied "horizon" where the photo
//   layer becomes fully opaque under the mask gradient — without
//   it the bottom edge of the text would visually collide with
//   the first row of fully-opaque photos.
// ``photoBaseSize`` — the canonical "100% scale" photo width in
//   pixels. Per-photo scale variation (see ``DEPTH_SCALES`` below)
//   multiplies this base, so a 0.55 entry on the chain yields a
//   photo about 55% the width of ``photoBaseSize``. Sized to give
//   ~3-4 photos visible on screen at any moment for the upward
//   sibling, ~4-5 for the lateral siblings (which are wider than
//   they are tall).
// ``laneOffsetPctVertical`` / ``laneOffsetPctHorizontal``
//   — per-photo cross-axis offset budget as a percentage of the
//   cell's own perpendicular dimension. The renderer multiplies
//   this base by a per-photo entry in ``LANE_OFFSET_CHAIN``
//   (signed, magnitude ≤ 1) so photos land at distinctly
//   different cross-axis positions instead of two tight parallel
//   lanes.
//
//   The split per axis exists because the vertical and lateral
//   siblings have very different perpendicular-axis geometry:
//
//     * Vertical drift: the perpendicular axis is X. The hero is
//       almost always wider than it is tall, so there's plenty of
//       horizontal room to push photos toward the left and right
//       edges. Generous budget (90-110%).
//
//     * Lateral drift (left / right): the perpendicular axis is
//       Y, and the horizontal viewport's mask gradient already
//       fades the upper portion of the band to transparent.
//       Pushing photos by a Y budget that big would shove half
//       the cluster into the faded region, making the spread
//       invisible. Smaller budget (40-55%) keeps every photo
//       within the opaque part of the mask while still landing
//       photos at clearly different top/bottom positions.
// ``gap`` — inter-photo gap on the drift axis. Bumped from the
//   earlier draft because the photos no longer need to fit
//   inside a tight banner-style band, so we let them breathe.
const BP_CONFIG = {
  extra_large: {
    contentPx: 64, contentPy: 96,
    photoBaseSize: 360,
    laneOffsetPctVertical: 110,
    laneOffsetPctHorizontal: 55,
    gap: 36,
  },
  large: {
    contentPx: 56, contentPy: 80,
    photoBaseSize: 305,
    laneOffsetPctVertical: 100,
    laneOffsetPctHorizontal: 50,
    gap: 32,
  },
  medium: {
    contentPx: 40, contentPy: 64,
    photoBaseSize: 255,
    laneOffsetPctVertical: 90,
    laneOffsetPctHorizontal: 45,
    gap: 26,
  },
  small: {
    contentPx: 24, contentPy: 48,
    photoBaseSize: 200,
    laneOffsetPctVertical: 70,
    laneOffsetPctHorizontal: 40,
    gap: 20,
  },
  extra_small: {
    contentPx: 16, contentPy: 36,
    photoBaseSize: 155,
    laneOffsetPctVertical: 55,
    laneOffsetPctHorizontal: 32,
    gap: 14,
  },
};

// Cap defensively against the backend ``PhotoDriftMode._DRIFT_MAX``
// (= 6). The renderer technically supports any count via the
// duplicated-track marquee, but past 6 the depth-scatter reads as
// noise and adjacent photos start visually merging at typical hero
// widths. The cap is the ceiling, not the recommendation: 4-6 is
// the sweet spot the mode description calls out.
const MAX_PHOTOS = 6;

// Per-photo scale multipliers applied in index order (wraps when
// there are more than ``DEPTH_SCALES.length`` photos). The chain
// interleaves "near" (1.00 / 0.92) and "far" (0.65 / 0.55) values
// so adjacent photos always differ noticeably in scale — the depth
// illusion needs that "this one is closer, this one is further"
// contrast across the eye's natural scan path. A monotonic ramp
// (1.0 → 0.5) would read as a tidy gradient, not a scattered
// composition. Tuned by eye against a 4-6 photo cluster; the
// extremes (0.55 / 1.00) frame the readable range — anything
// smaller stops registering as a "real photo at distance" and
// turns into a thumbnail.
const DEPTH_SCALES = [1.0, 0.62, 0.86, 0.55, 0.92, 0.7];

// Per-photo lateral offset multipliers applied in index order
// (wraps when there are more than ``LANE_OFFSET_CHAIN.length``
// photos). Signed so adjacent entries flip across the centre
// line, but with varied magnitudes so the photos don't read as
// "two parallel rails" — every entry lands at a noticeably
// different cross-axis position. The chain is tuned so that
// across a 4-6 photo cluster:
//
//   * Both extremes (-1.0, +1.0) are hit, so the cluster reaches
//     all the way to the cross-axis edges of its viewport budget
//     instead of crowding the middle.
//   * No two adjacent entries share the same magnitude, so the
//     scatter never momentarily resolves into a tidy zig-zag.
//   * The signed sequence has more sign-flips than a strict
//     alternation, which breaks the "two-lane" reading the
//     original ``idx % 2`` lane assignment kept producing.
//
// Multiplied by ``laneOffsetPct`` from the BP config; the result
// is the percentage translation on the perpendicular axis.
const LANE_OFFSET_CHAIN = [-1.0, 0.85, -0.45, 1.0, -0.78, 0.6];

// Upward drift has enough vertical runway for each source image to
// reappear later in the same marquee sequence. Left/right drift is
// confined to the lower horizontal band, so it uses one appearance
// per source image to avoid overcrowding that band.
const VERTICAL_APPEARANCES_PER_IMAGE = 2;
const HORIZONTAL_APPEARANCES_PER_IMAGE = 1;
const APPEARANCE_CHAIN_OFFSET = 3;

// Default loop duration overrides per breakpoint. The CSS variable
// ``--hero-photo-drift-duration`` defaults to 28s in
// ``heroModeAnimations.css``; we override per-breakpoint so the
// on-screen photo velocity stays roughly constant regardless of
// viewport size (smaller cards have less ground to cover per loop
// and so should loop faster to keep pace with the eye, the same
// reasoning Gallery Strip uses for ``--hero-marquee-duration``).
const TRACK_DURATION_BY_BP = {
  extra_large: "34s",
  large: "32s",
  medium: "28s",
  small: "24s",
  extra_small: "22s",
};

// ---------------------------------------------------------------------------
// HeroPhotoDrift main renderer
// ---------------------------------------------------------------------------

const HeroPhotoDrift = ({ item, heroBase, heroSlots }) => {
  const {
    heroRootRef, containerClasses, containerStyle, fullBleedStyle,
    contentInsetStyle, responsiveHeight, baseHeightPx, bp,
  } = heroBase;

  const isAnimated = heroSlots?.isAnimated || false;
  const gallerySlotData = heroSlots?.getSlot("gallery");
  const galleryItems = useMemo(() => {
    if (!gallerySlotData) return [];
    if (Array.isArray(gallerySlotData)) return gallerySlotData;
    return [gallerySlotData];
  }, [gallerySlotData]);
  const images = useMemo(
    () => galleryItems.filter((it) => it?.type === "image").slice(0, MAX_PHOTOS),
    [galleryItems],
  );
  const hasImages = images.length > 0;

  const config = BP_CONFIG[bp] || BP_CONFIG.medium;
  // Vertical sibling = upward drift (two columns side-by-side).
  // Lateral siblings (left/right drift) collapse to the horizontal
  // two-row band layout — they share every other piece of the
  // renderer (per-photo depth scatter, duplicated-track loop,
  // backdrop mask) and only differ in lane axis + the keyframe id
  // mounted via ``ambientClass``.
  const isVertical = item?.hero_mode === MODE_DRIFT_UP;

  // ``ambientClass`` returns "" under ``prefers-reduced-motion`` (or
  // when the mode declares ``ambient_animation = null``, which the
  // Photo Drift family never does). The class name encodes the drift
  // axis directly (one of ``hero-ambient-photo-drift-{up|left|right}``
  // — see ``heroModeAnimations.css``), so we just mount it on the
  // track wrapper without any extra branching here.
  const ambientClass = useHeroAmbient(heroSlots?.ambientAnimation);

  const entrance = heroSlots?.animatedEntrance || "fade-up";
  const contentAnim = useHeroAnimation(isAnimated, entrance, { delay: 200 });

  // The photo backdrop is mounted as ``position: absolute; inset:
  // 0`` inside the hero root so it lives at the same edge-to-edge
  // box as the ``Background`` layer (which is already the hero's
  // padding box, see ``Background.js``). That means it kisses the
  // hero's outer edges *without* needing the negative-margin
  // break-out dance Photo Banner / Magazine use for their
  // in-flow strips — there's no padding zone to escape.
  //
  // The "below the text" reading is achieved entirely via the CSS
  // mask gradient applied on ``.hero-photo-drift-viewport`` (see
  // ``heroPhotoDrift.css``): the upper portion of the photo layer
  // fades to transparent so the user's chosen background bleeds
  // through where the text sits, and the lower portion is fully
  // opaque so the photos read as a backdrop occupying the bottom
  // of the hero.

  // Build the photo descriptors once, expanding the source images
  // into appearance passes. Upward drift uses two passes ordered as
  // A, B, C, A, B, C so the same image does not reappear until the
  // rest of the set has nearly passed. Left/right drift uses a
  // single pass to keep the lower band from feeling overcrowded.
  //
  // Hoisted ABOVE the empty-state early return so this hook always
  // executes in the same order regardless of whether the gallery
  // slot is populated — React's rules-of-hooks forbid conditional
  // hook calls. Building the empty array in the no-images branch
  // is cheap and the descriptor is only consumed inside the
  // populated branch below.
  const appearancesPerImage = isVertical
    ? VERTICAL_APPEARANCES_PER_IMAGE
    : HORIZONTAL_APPEARANCES_PER_IMAGE;
  const photoCells = useMemo(
    () =>
      Array.from({ length: appearancesPerImage }).flatMap((_, appearanceIdx) =>
        images.map((img, idx) => {
          const chainIdx = idx + appearanceIdx * APPEARANCE_CHAIN_OFFSET;
          const sequenceIdx = appearanceIdx * images.length + idx;
          const scale = DEPTH_SCALES[chainIdx % DEPTH_SCALES.length];
          const laneMultiplier =
            LANE_OFFSET_CHAIN[chainIdx % LANE_OFFSET_CHAIN.length];

          return {
            img,
            idx,
            sequenceIdx,
            scale,
            laneMultiplier,
          };
        }),
      ),
    [images, appearancesPerImage],
  );

  const heroBackgroundLayer = (
    <Background
      bgImage={heroBase.bgImage}
      imageStyleClasses={heroBase.imageStyleClasses}
      imageInlineStyles={heroBase.imageInlineStyles}
      colorClass={heroBase.colorClass}
      colorStyle={heroBase.colorStyle}
      jsPatternLayer={heroBase.jsPatternLayer}
    />
  );

  // Empty state mirrors Photo Banner / Gallery: stack the text
  // centred so the editor still has something readable while the
  // user wires up images. The ``+ Add Image`` affordance is wired
  // inline so editors can populate the slot without leaving the
  // canvas.
  if (!hasImages) {
    return (
      <div
        ref={heroRootRef}
        data-hero-root="true"
        className={containerClasses}
        style={{ ...containerStyle, ...fullBleedStyle }}
      >
        {heroBackgroundLayer}
        <ContainerEffectOverlay data={heroBase.data} />
        <div
          className={
            responsiveHeight
              ? "relative z-10 flex items-center justify-center w-full"
              : "absolute inset-0 z-10 flex items-center justify-center"
          }
          style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
        >
          <div
            ref={contentAnim.ref}
            className={`flex flex-col gap-4 w-full max-w-2xl mx-auto ${contentAnim.className}`}
            style={{ padding: config.contentPx, ...contentAnim.style, ...contentInsetStyle }}
          >
            <EmptyMediaSlotAffordance
              heroSlots={heroSlots}
              heroId={item?.id}
              slotName="gallery"
              order={0}
            />
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
          </div>
        </div>
      </div>
    );
  }

  // Track CSS — directionally independent of the per-photo cells
  // (the cells flow inside the track regardless of axis), so we
  // resolve it once. The drift-direction class name is supplied by
  // ``ambientClass`` (one of ``hero-ambient-photo-drift-{up|left|right}``).
  // ``--hero-photo-drift-duration`` is set on the track itself so
  // the per-breakpoint speed override only applies to this hero —
  // a future Photo Drift inside another component on the same page
  // wouldn't accidentally inherit it.
  const trackDuration = TRACK_DURATION_BY_BP[bp] || TRACK_DURATION_BY_BP.medium;

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={{ ...containerStyle, ...fullBleedStyle }}
    >
      {heroBackgroundLayer}

      {/* Photo backdrop layer. Sits at ``z-[1]`` between the
       * hero ``Background`` (``z-0``) and the foreground content
       * (``z-10``) so the photos read as part of the scenery
       * rather than as a stacked block. ``absolute inset-0`` makes
       * the layer fill the hero's padding box edge-to-edge — same
       * containing-block contract the ``Background`` uses — so
       * the photos visually belong to the same shell as the
       * background colour / pattern.
       *
       * The hero's height is no longer driven by the photo track
       * (it was in the earlier draft, where the viewport sat in a
       * flex column with the text and demanded ``trackVh`` of
       * height): the absolute layer takes zero flow space, so the
       * hero's box collapses back to whatever the text content
       * column needs.
       *
       * ``pointer-events: none`` lets editors click straight
       * through to the text slots beneath without the photo layer
       * intercepting selection — the photos are scenery, not
       * interactive content. */}
      <div
        className={`hero-photo-drift-viewport hero-photo-drift-viewport--backdrop absolute inset-0 z-[1] overflow-hidden pointer-events-none ${
          isVertical
            ? "hero-photo-drift-viewport--vertical"
            : "hero-photo-drift-viewport--horizontal"
        }`}
      >
        <div
          className={`hero-photo-drift-track hero-photo-drift-track--${
            isVertical ? "vertical" : "horizontal"
          } ${ambientClass}`}
          style={{ "--hero-photo-drift-duration": trackDuration }}
        >
          {/* Two copies of the photo list — the keyframe loops at
              ±50% so the second copy sits exactly where the eye
              lands at the seam, making the loop visually
              continuous. ``aria-hidden`` on the duplicate keeps
              screen readers from announcing the same photos
              twice. */}
          {[0, 1].map((copy) => (
            <div
              key={`copy-${copy}`}
              className={`hero-photo-drift-copy hero-photo-drift-copy--${
                isVertical ? "vertical" : "horizontal"
              }`}
              aria-hidden={copy === 1 ? "true" : undefined}
              style={{ gap: `${config.gap}px` }}
            >
              {photoCells.map(({ img, idx, sequenceIdx, scale, laneMultiplier }) => {
                const src = img.cropped_image_url || img.image_url;
                // Per-photo wrapper carries the depth-scatter
                // styles (scale + lane offset). We use CSS variables
                // on the wrapper so the per-axis CSS rule
                // (``hero-photo-drift-cell--vertical`` /
                // ``--horizontal``) can pick which axis the
                // offset applies to. Motion stays on the parent
                // marquee only, which keeps every photo moving at
                // one clean constant velocity from start to end.
                const cellSize = Math.round(config.photoBaseSize * scale);
                // Per-axis lane budget: the vertical sibling uses
                // the bigger ``laneOffsetPctVertical`` (X has lots
                // of room); the lateral siblings use the smaller
                // ``laneOffsetPctHorizontal`` (Y is constrained
                // by the band's mask). See ``BP_CONFIG`` notes.
                const laneOffsetPct = isVertical
                  ? config.laneOffsetPctVertical
                  : config.laneOffsetPctHorizontal;
                const laneOffset = laneMultiplier * laneOffsetPct;
                return (
                  <div
                    key={`drift-${img.id || idx}-${copy}-${sequenceIdx}`}
                    className={`relative hero-photo-drift-cell hero-photo-drift-cell--${
                      isVertical ? "vertical" : "horizontal"
                    }`}
                    style={{
                      // ``--hero-drift-cell-size`` = the photo's
                      // width (vertical sibling) or height
                      // (lateral siblings). The CSS class picks
                      // which dimension binds to this var.
                      "--hero-drift-cell-size": `${cellSize}px`,
                      // ``--hero-drift-lane-offset`` = the
                      // perpendicular-axis translation in
                      // percentage units. Sign + magnitude come
                      // from ``laneMultiplier * laneOffsetPct``;
                      // the CSS converts it into a ``translate``
                      // on the correct axis.
                      "--hero-drift-lane-offset": `${laneOffset}%`,
                    }}
                  >
                    {src ? (
                      (() => {
                        const { className: filterClass, style: filterStyle } =
                          getHeroImageStyle(img);
                        return (
                          <ProgressiveHeroImage
                            src={src}
                            alt={img.alt_text || ""}
                            loading={sequenceIdx === 0 && copy === 0 ? "eager" : "lazy"}
                            draggable={false}
                            className={`hero-photo-drift-img ${filterClass}`.trim()}
                            style={filterStyle}
                          />
                        );
                      })()
                    ) : (
                      <div className="hero-photo-drift-img hero-photo-drift-img--empty" />
                    )}
                    <EditableSlotMediaOverlay slotData={img} heroItem={item} />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <SlotCapacityAffordance
        heroSlots={heroSlots}
        heroId={item?.id}
        slotName="gallery"
        currentCount={images.length}
        className="absolute left-1/2 top-6 z-[2] w-full max-w-xs -translate-x-1/2 px-4"
      />

      <ContainerEffectOverlay data={heroBase.data} />

      {/* Foreground text column — sits at ``z-10`` above the
       * photo backdrop. Normal flow so the hero's height collapses
       * to whatever the text content needs (the photo backdrop is
       * absolute and contributes nothing to the box).
       *
       * ``min-w-0`` mirrors the Split / Photo Banner treatment to
       * protect against intrinsic-wide children (button rows, long
       * words) blowing past the visible width.
       *
       * Under ``responsiveHeight: false`` we still anchor the text
       * column with ``absolute inset-0`` so the user's fixed hero
       * height is honoured (mirrors the empty-state branch above
       * and matches every other hero mode's fixed-height
       * contract). */}
      <div
        className={
          responsiveHeight
            ? "relative z-10 w-full"
            : "absolute inset-0 z-10 flex items-center justify-center"
        }
        style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
      >
        <div
          ref={contentAnim.ref}
          className={`flex flex-col gap-4 min-w-0 ${contentAnim.className}`}
          style={{
            paddingLeft: config.contentPx,
            paddingRight: config.contentPx,
            paddingTop: config.contentPy,
            paddingBottom: config.contentPy,
            ...contentAnim.style,
            ...(responsiveHeight ? null : contentInsetStyle),
          }}
        >
          <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
          <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
          <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroPhotoDrift);
